import { VdFeatureManager, VdTracking } from '@vend/vend.js'
import { IScope } from 'angular'
import find from 'lodash/find'
import filter from 'lodash/filter'
import flatten from 'lodash/flatten'
import flowRight from 'lodash/flowRight'
import map from 'lodash/map'
import merge from 'lodash/merge'
import uniq from 'lodash/uniq'

import { vn, add, subtract } from 'vend-number'
import { ApiCustomer, ApiProduct, ApiUser } from '@vend/types'

import { EVENTS } from '../../../../constants'
import { SaleStatus } from '../../../../models/sale.constants'
import { ROUTES } from '../../../../../../lib/routes'
import { $log, $q, $rootScope } from '../../../../ng-compat/imports'
import { Sale } from '../../../../models/sale'
import { SaleTransformer } from '../../../../factories/sale-transformer'
import { UsersStore } from '../../../../factories/database/users-store'
import { RegisterManager } from '../../../../managers/register-manager'
import { WebRegisterStatusManager } from '../../../../managers/web-register-status-manager'
import { RetailerSettingsManager } from '../../../../managers/retailer-settings-manager'
import { CustomersStore } from '../../../../factories/database/customers-store'
import { SearchResource } from '../../../search/search-resource/search-resource.service'

const getUniqueEntries = flowRight(uniq, filter, map)
const MAX_PRODUCT_SUMMARY = 2

/**
 * Controller Class for RetrieveSalePopover. Retrieves the latest parked sales and shows a summary of each.
 */
export class RetrieveSalePopoverController {
  public discountProductId: $TSFixMe
  public errorRetrievingSales: $TSFixMe
  public maxSales: $TSFixMe
  public placeHolderImageSrc: $TSFixMe
  public sales: $TSFixMe
  public salesRetrieved: $TSFixMe

  constructor(
    private $scope: IScope,
    public close: $TSFixMe,
    private registerManager: RegisterManager,
    private retailerSettingsManager: RetailerSettingsManager,
    private saleTransformer: SaleTransformer,
    private searchResource: SearchResource,
    private usersStore: UsersStore,
    private webRegisterStatusManager: WebRegisterStatusManager,
    private vdTracking: VdTracking,
    private vdFeatureManager: VdFeatureManager,
    private customersStore: CustomersStore
  ) {
    'ngInject'
    /**
     * Collection of recent parked sales that can be continued.
     *
     * @property sales
     * @type {Array}
     */
    this.sales = []

    /**
     * The maximum number of parked sales to show.
     *
     * @property maxSales
     * @type {Number}
     */
    this.maxSales = 10

    /**
     * Flag to identify if sales have been retrieved.
     *
     * @property salesRetrieved
     * @type {Boolean}
     */
    this.salesRetrieved = false

    /**
     * The fallback image used for when a parked sale has no products.
     *
     * @property placeHolderImageSrc
     * @type {String}
     */
    this.placeHolderImageSrc = 'products/placeholder.svg'

    /**
     * The id of the discount product. We ignore it when presenting products within a parked sale.
     *
     * @property discountProductId
     * @type {String}
     */
    this.discountProductId = this.retailerSettingsManager.get().discount_product_id
    /**
     * Flag to indicate that an error occured during the retrieval of parked sales.
     *
     * @property errorRetrievingSales
     * @type {Boolean}
     */
    this.errorRetrievingSales = false

    this._init()
  }

  /**
   * Transition to the sales history and shows all parked sales.
   *
   * @method showAllParkedSales
   *
   * @returns {Promise} Resolved once the state has been changed.
   */
  public showAllParkedSales() {
    this.vdTracking.trackUserEvent({
      eventName: 'Retrieve Sale Popover: Show All Parked Sales',
    })

    window.location.href = `${ROUTES.NEW_SALES_HISTORY_CONTINUE}?status=SAVED`
  }

  /**
   * Closes the popover and emits the id of the sale to be loaded.
   *
   * @method continueSale
   *
   * @param {Object} sale
   *        The sale to be loaded.
   */
  public continueSale(sale: $TSFixMe) {
    this.vdTracking.trackUserEvent({
      eventName: 'Retrieve Sale Popover: Continue Sale',
      metadata: {
        saleId: sale.id,
      },
    })

    this.close()
    $rootScope.$emit('retrieveSale:loadSale', sale.id)
  }

  /**
   * Helper method to determine if there is a valid customer associated with the sale.
   *
   * @method hasValidCustomer
   *
   * @param {Object} sale
   *        Api representation of the sale.
   *
   *        @param {Object} sale.customer
   *              The customer associated with the sale.
   *
   * @returns {Boolean} True if customer is valid, false otherwise.
   */
  public hasValidCustomer({ customer }: { customer: $TSFixMe }) {
    return Boolean(customer && customer.customer_code !== 'WALKIN')
  }

  /**
   * Initialise the popover.
   *
   * @private
   * @method _init
   */
  private _init() {
    this.vdTracking.trackUserEvent({
      eventName: 'Retrieve Sale Popover Shown',
    })

    this._closePopoverWhenOffline()
    this._retrieveSales()
  }

  /**
   * Binds the required events needed to ensure that the popover is closed when the register transitions offline.
   *
   * @private
   * @method _closePopoverWhenOffline
   */
  private _closePopoverWhenOffline() {
    this.webRegisterStatusManager.on(EVENTS.statusChange, this.close, this)
    this.$scope.$on('$destroy', () => {
      this.webRegisterStatusManager.off(EVENTS.statusChange, this.close)
    })
  }

  /**
   * Requests the latest parked sales from the api and then hydrates each sale with its user, products and customer.
   *
   * @private
   * @method _retrieveSales
   *
   * @returns {Promise} Resolved once the sales have been retrieved.
   */
  private _retrieveSales() {
    const activeOutlet = this.registerManager.getActiveOutlet()
    const activeOutletId = activeOutlet && activeOutlet.id
    const useReadApi = this.vdFeatureManager.get('parked_sales_from_read_api')
    const sales: Record<string, number | string> = {
      outlet_id: activeOutletId,
      // Requesting one more than the max so we are aware if more parked sales are available (other than those shown)
      page_size: this.maxSales + 1,
    }

    if (useReadApi) {
      sales.status = SaleStatus.PARKED.toLowerCase()
    } else {
      sales['status[]'] = SaleStatus.PARKED.toLowerCase()
    }
    return this.searchResource
      .sales(sales)
      .then((sales: $TSFixMe) => this._hydrateSales(sales))
      .then((sales: $TSFixMe) => {
        this.sales = sales
        this.salesRetrieved = true
      })
      .catch((error: $TSFixMe) => {
        $log.warn(
          '[RetrieveSalePopover] Error when retrieving parked sales',
          error
        )

        this.errorRetrievingSales = true
      })
  }

  /**
   * Helper which organises the hydration of all the provided sales.
   *
   * @private
   * @method _hydrateSales
   *
   * @param {Array} sales
   *        Collection of retrieved parked sales from the api.
   *
   * @returns {Promise} Resolved once all of the sales have been hydrated.
   */
  private _hydrateSales(sales: Sale[]) {
    return $q
      .all({
        customers: this._getCustomers(sales),
        products: this._getProducts(sales),
        users: this._getUsers(sales),
      })
      .then(({ customers, products, users }) =>
        this._setSaleData({ sales, customers, products, users } as $TSFixMe)
      )
      .catch(error => {
        $log.warn('[RetrieveSalePopover] Error when hydrating sales', error)

        return $q.reject(error)
      })
  }

  /**
   * Sets the hydrated data for each sale.
   *
   * @private
   * @method _setSaleData
   *
   * @param {Object} data
   *
   *        @param {Array<Object>} data.sales
   *               The sales to hydrate.
   *
   *        @param {Array<Customer>} data.customers
   *               The unique collection of customers associated with the provided sales.
   *
   *        @param {Array<Products>} data.products
   *               The unique collection of products associated with the provided sales.
   *
   *        @param {Array<Users>} data.users
   *               The unique collection of users associated with the provided sales.
   *
   * @returns {Array<Object>} Collection of sales, fully hydrated.
   */
  private _setSaleData({
    sales,
    customers,
    products,
    users,
  }: {
    sales: $TSFixMe[]
    customers: ApiCustomer[]
    products: ApiProduct[]
    users: ApiUser[]
  }) {
    return sales.map(sale => {
      sale.customer = find(customers, { id: sale.customer_id })
      sale.user = find(users, { id: sale.user_id })
      sale.products = this._setProducts(sale, products)
      sale.productsSummary = this._setProductsSummary(sale.products)
      return sale
    })
  }

  /**
   * Constructs a collection of products from the line items in the provided sale. Note that when there are two
   * identical products, but with differing quantity orientation (one has a negative quantity, the other a positive
   * quantity) then these are treated as two separate products. It allows for each to be represented in the summary;
   * e.g. "1 x Product A", "-1 x Product A".
   *
   * @private
   * @method _setProducts
   *
   * @param {Object} sale
   *        The sale to evaluate.
   *
   * @param {Array<Products>} products
   *        The collection of products which to extract the required products from.
   *
   * @returns {Array<Products>} Collection of products associated with the sale.
   */
  private _setProducts(sale: $TSFixMe, products: ApiProduct[]) {
    const productsInSale: $TSFixMe[] = []
    sale.line_items.forEach((lineItem: $TSFixMe) => {
      // Skip if this is a discount product
      if (lineItem.product_id === this.discountProductId) {
        return
      }

      const lineItemHasNegQty = vn(lineItem.quantity).isNeg()

      // Establish if there is already an existing product with a matching quantity orientation (positive or negative).
      const saleProduct = find(productsInSale, product => {
        const saleProductHasNegQty = vn(product.quantity).isNeg()

        return (
          product.id === lineItem.product_id &&
          ((lineItemHasNegQty && saleProductHasNegQty) ||
            (!lineItemHasNegQty && !saleProductHasNegQty))
        )
      })

      if (!saleProduct) {
        const productToAddToSale = find(products, { id: lineItem.product_id })

        productsInSale.push(
          merge({}, productToAddToSale, { quantity: lineItem.quantity })
        )
        return
      }

      if (lineItemHasNegQty) {
        // Subtract the quantity from the existing product which has a negative orientated quantity
        saleProduct.quantity = subtract(
          saleProduct.quantity,
          vn(lineItem.quantity).abs().toNumber()
        )
      } else {
        // Add the quantity to the existing product
        saleProduct.quantity = add(saleProduct.quantity, lineItem.quantity)
      }
    })

    return productsInSale
  }

  /**
   * Generates and sets the product summary for each sale. This is limited to showing a maximum of 3 products. When
   * there are more than 3 the last summary will be a description of how many additional products are in the sale.
   *
   * @private
   * @method _setProductsSummary
   *
   * @param {Array<Products>} products
   *        The collection of products to summarise.
   *
   * @returns {Array<String>} Collection of products summary.
   */
  private _setProductsSummary(products: $TSFixMe[]) {
    const productsSummary: $TSFixMe[] = []
    products.some(product => {
      if (
        productsSummary.length === MAX_PRODUCT_SUMMARY &&
        products.length > MAX_PRODUCT_SUMMARY + 1
      ) {
        productsSummary.push(
          `and ${products.length - MAX_PRODUCT_SUMMARY} more products`
        )
        return true
      } else {
        const productName = product.hasFailedToLoad
          ? 'Product not found'
          : product.variant_name
        productsSummary.push(`${product.quantity} × ${productName}`)
        return undefined
      }
    })

    return productsSummary
  }

  /**
   * Gets the unique collection of products associated with the provided sales.
   *
   * @private
   * @method _getProducts
   *
   * @param {Array<Object>} sales
   *        The collection of hydrated sales.
   *
   * @returns {Promimse<Array<Products>>} Collection of unique products associated with the provided sales.
   */
  private _getProducts(sales: $TSFixMe[]) {
    const lineItems = flatten(sales.map(sale => sale.line_items))
    const productIds = getUniqueEntries(lineItems, 'product_id')

    return $q.all(
      productIds.map((productId: $TSFixMe) => {
        return this.saleTransformer.hydrateProduct(productId).then(product => {
          if (!product) {
            return {
              id: productId,
              hasFailedToLoad: true,
            }
          }
          // Variant products don't have any product images, these are all stored on the parent product
          if (product.variant_parent_id) {
            return this._getVariantParentProductImages(product).then(
              (parentProductImages: $TSFixMe) => {
                product.images = parentProductImages
                return product
              }
            )
          } else {
            return product
          }
        })
      })
    )
  }

  /**
   * Retrieves the product images from the product parent of a variant. This allows us to show images for
   * variant products.
   *
   * @private
   * @method _getVariantParentProductImages
   *
   * @param {Product} product
   *        The variant product.
   *
   * @returns {Promise<Array>} Resolved once the images for a parent variant product have been retrieved.
   */
  private _getVariantParentProductImages(product: ApiProduct) {
    return this.saleTransformer
      .hydrateProduct(product.variant_parent_id!)
      .then((product: $TSFixMe) => {
        return (product && product.images) || []
      })
  }

  /**
   * Gets the unique collection of customers associated with the provided sales.
   *
   * @private
   * @method _getCustomers
   *
   * @param {Array<Object>} sales
   *        The collection of hydrated sales.
   *
   * @returns {Promise<Array<Customers>>} Collection of unique customers associated with the provided sales.
   */
  private _getCustomers(sales: Sale[]) {
    const customerIds: string[] = getUniqueEntries(sales, 'customer_id')
    return $q.all(
      customerIds.map(customerId => this.customersStore.get(customerId)!)
    )
  }

  /**
   * Gets the unique collection of users associated with the provided sales.
   *
   * @private
   * @method _getUsers
   *
   * @param {Array<Object>} sales
   *        The collection of hydrated sales.
   *
   * @returns {Promise<Array<Users>>} Collection of unique users associated with the provided sales.
   */
  private _getUsers(sales: Sale[]) {
    const userIds = getUniqueEntries(sales, 'user_id')
    return $q.all(
      userIds.map((userId: $TSFixMe) => this.usersStore.get(userId))
    )
  }
}
